<template>
    <div class="upselling-options">
        <div>
            <header class="upselling-options__title">{{ $t('UpsellingOptions.selectDuration') }}</header>

            <ul class="upselling-options__list list-unstyled">
                <li v-for="(variant, index) in durationVariants" :key="'duration' + index">
                    <VariantItemOption
                        :title="variant.duration"
                        :person-price="!isPartnerShopWithPackagePricing ? variant.diffPersonPrice : undefined"
                        :package-price="isPartnerShopWithPackagePricing ? variant.diffPrice : undefined"
                        :surcharge="isShopWithSurcharge ? variant.diffSurcharge : undefined"
                        :subtitle="showSeparatePersonsSelection ? '' : variant.persons"
                        :active="selectedDuration === variant.duration"
                        @click.native="onVariantSelect(variant)"
						@keydown.enter.native="onVariantSelect(variant)"
						@keydown.space.native.prevent="onVariantSelect(variant)"
                    />
                </li>
            </ul>
        </div>

        <div v-if="showSeparatePersonsSelection">
            <h4 class="upselling-options__title">{{ $t('UpsellingOptions.selectNumberOfPersons') }}</h4>

            <ul class="upselling-options__list list-unstyled">
                <li v-for="(variant, index) in personsVariantsSorted" :key="'persons' + index">
                    <VariantItemOption
                        :title="variant.persons"
                        :person-price="!isPartnerShopWithPackagePricing ? variant.diffPersonPrice : undefined"
                        :package-price="isPartnerShopWithPackagePricing ? variant.diffPrice : undefined"
                        :surcharge="isShopWithSurcharge ? variant.diffSurcharge : undefined"
                        :active="selectedPersons === variant.numberOfPersons"
                        @click.native="onVariantSelect(variant)"
						@keydown.enter.native="onVariantSelect(variant)"
						@keydown.space.native.prevent="onVariantSelect(variant)"
                    />
                </li>
            </ul>
        </div>

        <div v-if="showCateringOrRoomTypeText">
            <div>
                <h4 class="upselling-options__title">
                    {{ $t('UpsellingOptions.allOffersInclude') }} <br />
                    <strong v-if="singleRoomType">{{ selectedRoomType }}</strong>
                    <span v-if="singleRoomType && singleCatering">{{ $t('Generic.and') }}</span>
                    <strong v-if="singleCatering">{{ selectedCatering }}</strong>
                </h4>
            </div>
        </div>

        <div v-if="!singleRoomType">
            <h4 class="upselling-options__title">{{ $t('UpsellingOptions.selectRoomType') }}</h4>

            <ul class="upselling-options__list list-unstyled">
                <li v-for="(variant, index) in roomTypeVariantsSorted" :key="'roomType' + index">
                    <VariantItemOption
                        :title="variant.roomType"
                        :person-price="!isPartnerShopWithPackagePricing ? variant.diffPersonPrice : undefined"
                        :package-price="isPartnerShopWithPackagePricing ? variant.diffPrice : undefined"
                        :surcharge="isShopWithSurcharge ? variant.diffSurcharge : undefined"
                        :active="selectedRoomType === variant.roomType"
                        @click.native="onVariantSelect(variant)"
						@keydown.enter.native="onVariantSelect(variant)"
						@keydown.space.native.prevent="onVariantSelect(variant)"
                    />
                </li>
            </ul>
        </div>

        <div v-if="!singleCatering">
            <h4 class="upselling-options__title">{{ $t('UpsellingOptions.selectCatering') }}</h4>

            <ul class="upselling-options__list list-unstyled">
                <li v-for="(variant, index) in cateringVariantsSorted" :key="'catering' + index">
                    <VariantItemOption
                        :title="variant.catering"
                        :person-price="!isPartnerShopWithPackagePricing ? variant.diffPersonPrice : undefined"
                        :package-price="isPartnerShopWithPackagePricing ? variant.diffPrice : undefined"
                        :surcharge="isShopWithSurcharge ? variant.diffSurcharge : undefined"
                        :active="selectedCatering === variant.catering"
                        :hint="!showMarketingHingInAdditionalFeatures ? variant.marketingHint : null"
                        :dense="!showMarketingHingInAdditionalFeatures && !!variant.marketingHint"
                        @click.native="onVariantSelect(variant)"
						@keydown.enter.native="onVariantSelect(variant)"
						@keydown.space.native.prevent="onVariantSelect(variant)"
                    />
                </li>
            </ul>
        </div>

        <div v-if="!!additionalFeatureVariants.length">
            <h4 class="upselling-options__title">{{ $t('UpsellingOptions.additionalFeatures') }}</h4>

            <ul class="upselling-options__list list-unstyled">
                <li v-for="(variant, index) in additionalFeatureVariants" :key="'additionalFeature' + index">
                    <VariantItemOption
                        :title="variant.additionalFeature"
                        :package-price="variant.diffPrice"
                        :surcharge="isShopWithSurcharge ? variant.diffSurcharge : undefined"
                        :active="selectedAdditionalFeature === variant.additionalFeature"
                        :dense="showMarketingHingInAdditionalFeatures && !!variant.marketingHint"
                        @click.native="onFeatureSelect(variant)"
						@keydown.enter.native="onFeatureSelect(variant)"
						@keydown.space.native.prevent="onFeatureSelect(variant)"
                        :hint="showMarketingHingInAdditionalFeatures ? variant.marketingHint : null"
                    />
                </li>
            </ul>
        </div>
    </div>
</template>

<script>
import VariantItem from './VariantItem.vue';
import VariantItemOption from '@/components/product/selection/VariantItemOption.vue';

/**
 * @typedef { import('../../../models/pre-ts/product/Variant').Variant } Variant
 */

/**
 * @typedef { Object } VariantDiffPrice
 * @extends Variant
 * @property { number } diffPersonPrice
 */

export default {
    name: 'UpsellingOptions',

    components: { VariantItemOption, VariantItem },

    data() {
        return {
            previousVariant: null // required for unchecking additional feature
        };
    },

    props: {
        /**
         * @type { Variant[] }
         */
        variants: {
            type: Array,
            required: true
        },

        /**
         * @type { Variant }
         */
        selectedVariant: {
            type: Object,
            required: true
        },

        /**
         * @type { boolean }
         *  Controls visibility of options that are not selectable within current combination
         */
        showSelectableOnly: {
            type: Boolean,
            default: true
        }
    },

    computed: {
        /**
         * Main set of variants that is independent of selection
         * @returns {VariantDiffPrice[]}
         */
        durationVariants() {
            const getSafe = this.$getSafe;
            // selected variant should be first. This ensures it does not get filtered out,
            // thus clicking on selected option will not trigger another variant selection
            const durationsWithSelectedFirst = [this.selectedVariant, ...this.variants];
            return (
                durationsWithSelectedFirst
                    // filtering out unique duration options
                    .filter((variant, index, variants) => {
                        return variants.findIndex(v => v.duration === variant.duration) === index;
                    })
                    // cheaper first
                    .sort((v1, v2) => v1.numberOfNights - v2.numberOfNights)
                    // calculating diff price
                    .map(v => {
                        return {
                            ...v,
                            diffPersonPrice:
                                getSafe(v, 'price.regularPriceValues.perPersonPrice') - this.selectedPerPersonPrice,
                            diffPrice: getSafe(v, 'price.regularPriceValues.price') - this.selectedPackagePrice,
                            diffSurcharge: getSafe(v, 'price.surcharge') - this.selectedSurcharge
                        };
                    })
            );
        },

        /**
         * @returns {Variant[]}
         */
        filteredBySelectedDuration() {
            return this.variants.filter(v => v.duration === this.selectedDuration);
        },

        showSeparatePersonsSelection() {
            return this.personsVariantsSorted.length > 1;
        },

        /**
         * @returns {VariantDiffPrice[]}
         */
        personsVariantsSorted() {
            const getSafe = this.$getSafe;
            // selected variant should be first. This ensures it does not get filtered out,
            // thus clicking on selected option will not trigger another variant selection
            let personsVariants = [this.selectedVariant, ...this.filteredBySelectedDuration];
            // filtering out unique persons options
            personsVariants = personsVariants.filter((variant, index, variants) => {
                return variants.findIndex(v => v.numberOfPersons === variant.numberOfPersons) === index;
            });
            // cheaper first
            const sortedByPrice = personsVariants.sort((a, b) => {
                if (this.isShopWithSurcharge) {
                    return this.$getSafe(a, 'price.surcharge') - this.$getSafe(b, 'price.surcharge');
                }
                return (
                    this.$getSafe(a, 'price.regularPriceValues.price') -
                    this.$getSafe(b, 'price.regularPriceValues.price')
                );
            });
            // calculating diff price
            return sortedByPrice.map(v => {
                return {
                    ...v,
                    diffPersonPrice:
                        getSafe(v, 'price.regularPriceValues.perPersonPrice') - this.selectedPerPersonPrice,
                    diffPrice: getSafe(v, 'price.regularPriceValues.price') - this.selectedPackagePrice,
                    diffSurcharge: getSafe(v, 'price.surcharge') - this.selectedSurcharge
                };
            });
        },

        /**
         * @returns {VariantDiffPrice[]}
         */
        roomTypeVariantsSorted() {
            const getSafe = this.$getSafe;
            let roomTypeVariants = [...this.filteredBySelectedDuration];
            if (this.showSelectableOnly) {
                roomTypeVariants = roomTypeVariants.filter(v => {
                    return v.catering === this.selectedCatering && v.numberOfPersons === this.selectedPersons;
                });
            }
            // selected variant should be first. This ensures it does not get filtered out,
            // thus clicking on selected option will not trigger another variant selection
            roomTypeVariants = [this.selectedVariant, ...roomTypeVariants];
            // filtering out unique room type options
            roomTypeVariants = roomTypeVariants.filter((variant, index, variants) => {
                return variants.findIndex(v => v.roomType === variant.roomType) === index;
            });

            return (
                roomTypeVariants
                    // cheaper first
                    .sort((a, b) => {
                        if (this.isShopWithSurcharge) {
                            return this.$getSafe(a, 'price.surcharge') - this.$getSafe(b, 'price.surcharge');
                        }
                        return (
                            this.$getSafe(a, 'price.regularPriceValues.price') -
                            this.$getSafe(b, 'price.regularPriceValues.price')
                        );
                    })
                    // calculating diff price
                    .map((v, i, variants) => {
                        return {
                            ...v,
                            diffPersonPrice:
                                getSafe(v, 'price.regularPriceValues.perPersonPrice') - this.selectedPerPersonPrice,
                            diffPrice: getSafe(v, 'price.regularPriceValues.price') - this.selectedPackagePrice,
                            diffSurcharge: getSafe(v, 'price.surcharge') - this.selectedSurcharge
                        };
                    })
            );
        },

        /**
         * @returns {VariantDiffPrice[]}
         */
        cateringVariantsSorted() {
            const getSafe = this.$getSafe;
            let cateringVariants = [...this.filteredBySelectedDuration];

            if (this.showSelectableOnly) {
                cateringVariants = cateringVariants.filter(v => {
                    return v.roomType === this.selectedRoomType && v.numberOfPersons === this.selectedPersons;
                });
            }
            // selected variant should be first. This ensures it does not get filtered out
            // thus clicking on selected option will not trigger another variant selection
            cateringVariants = [this.selectedVariant, ...cateringVariants];
            // filtering out unique catering options
            cateringVariants = cateringVariants.filter((variant, index, variants) => {
                return variants.findIndex(v => v.catering === variant.catering) === index;
            });

            return (
                cateringVariants
                    // cheaper first
                    .sort((a, b) => {
                        if (this.isShopWithSurcharge) {
                            return this.$getSafe(a, 'price.surcharge') - this.$getSafe(b, 'price.surcharge');
                        }
                        return (
                            this.$getSafe(a, 'price.regularPriceValues.price') -
                            this.$getSafe(b, 'price.regularPriceValues.price')
                        );
                    })
                    // calculating diff price
                    .map((v, i, variants) => {
                        return {
                            ...v,
                            diffPersonPrice:
                                getSafe(v, 'price.regularPriceValues.perPersonPrice') - this.selectedPerPersonPrice,
                            diffPrice: getSafe(v, 'price.regularPriceValues.price') - this.selectedPackagePrice,
                            diffSurcharge: getSafe(v, 'price.surcharge') - this.selectedSurcharge
                        };
                    })
            );
        },

        additionalFeatureVariants() {
            const getSafe = this.$getSafe;
            const variantsWithAdditionalFeature = this.variants.filter(v => !!v.additionalFeature);
            // grouping similar additional features by duration. E.g. same feature in different duration options
            const variantsWithAdditionalFeatureSimilarToSelected = variantsWithAdditionalFeature.filter(variant => {
                return (
                    variant.duration === this.selectedDuration &&
                    variant.roomType === this.selectedRoomType &&
                    variant.numberOfPersons === this.selectedPersons &&
                    variant.catering === this.selectedCatering
                );
            });
            // filtering out unique additional feature options
            const variantsWithAdditionalFeatureAmongSelectedDurationUnique =
                variantsWithAdditionalFeatureSimilarToSelected.filter((variant, index, variants) => {
                    return variants.findIndex(v => v.additionalFeature === variant.additionalFeature) === index;
                });
            // calculating diff price
            return variantsWithAdditionalFeatureAmongSelectedDurationUnique.map(v => {
                return {
                    ...v,
                    diffPersonPrice:
                        getSafe(v, 'price.regularPriceValues.perPersonPrice') - this.selectedPerPersonPrice,
                    diffPrice: getSafe(v, 'price.regularPriceValues.price') - this.selectedPackagePrice,
                    diffSurcharge: getSafe(v, 'price.surcharge') - this.selectedSurcharge
                };
            });
        },

        selectedPersons() {
            return this.selectedVariant.numberOfPersons;
        },

        selectedDuration() {
            return this.selectedVariant.duration;
        },

        selectedRoomType() {
            return this.selectedVariant.roomType;
        },

        selectedCatering() {
            return this.selectedVariant.catering;
        },

        selectedAdditionalFeature() {
            return this.selectedVariant.additionalFeature;
        },

        selectedPackagePrice() {
            return this.$getSafe(this.selectedVariant, 'price.regularPriceValues.price');
        },

        selectedPerPersonPrice() {
            return this.$getSafe(this.selectedVariant, 'price.regularPriceValues.perPersonPrice');
        },

        selectedSurcharge() {
            return this.$getSafe(this.selectedVariant, 'price.surcharge');
        },

        showMarketingHingInAdditionalFeatures() {
            return this.additionalFeatureVariants.some(v => v.marketingHint);
        },

        showCateringOrRoomTypeText() {
            return this.singleCatering || this.singleRoomType;
        },

        singleRoomType() {
            return this.roomTypeVariantsSorted.length === 1;
        },

        singleCatering() {
            return this.cateringVariantsSorted.length === 1;
        },

        isPartnerShopWithPackagePricing() {
            return this.$store.getters.isPartnerShopWithPackagePricing;
        },

        isShopWithSurcharge() {
            return this.$store.state.globalMeta.surchargeEnabled;
        }
    },

    methods: {
        onVariantSelect(variant) {
            this.$emit('variantSelected', variant);
        },

        onFeatureSelect(variant) {
            let selectedFeatureVariant = variant;

            // if selected variant is the same as the one we are selecting, we want to select the previous variant
            if (this.selectedVariant.id === variant.id && this.previousVariant) {
                selectedFeatureVariant = this.previousVariant;
            }

            this.onVariantSelect(selectedFeatureVariant);
        }
    },

    watch: {
        selectedVariant(newVariant, prevVariant) {
            if (prevVariant) {
                this.previousVariant = prevVariant;
            }
        }
    }
};
</script>

<style scoped lang="scss">
.upselling-options {
    &__title {
        text-align: center;
        margin: 25px 0 23px;
        font-size: 16px;
        font-weight: 400;
        line-height: 1.2;
    }

    &__list {
        margin-bottom: 0;
        background-color: var(--product-select-variant-bg);
    }
}
</style>
